import { Col, Row, Spin } from "antd";
import ListFilter from "components/ListFilter/ListFilter";
import { AntdTablePagination } from "components/Table/Table.d";
import {
  LocationFilterInput,
  LocationsForCustomerQuery,
  SortOptions,
  useLocationsForCustomerQuery,
} from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "services/auth/authService";
import useMenuNavigation from "../../../hooks/use-menu-navigation";
import useRoleAndPermission from "../../../hooks/use-role-and-permission";
import { getSurveyDataByEmail } from "../../../utils/indexedDB";
import { getPaginationMessage } from "../Location.helper";
import { Location } from "./LocationList.d";
import LocationListTable from "./LocationListTable";

import logger from "../../../services/logger/logger";

const LocationList = () => {
  const defaultPagination = { current: 1, pageSize: 10, total: 0 };
  const [pagination, setPagination] = useState<AntdTablePagination>(defaultPagination);
  const [tableData, setTableData] = useState<Location[]>([]);
  const [filter, setFilter] = useState<LocationFilterInput>({});
  const menuNavigation = useMenuNavigation();
  const { user, isAuthenticated } = useAuth0();
  const [checkingPendingData, setCheckingPendingData] = useState<boolean>(true);
  const [redirectToSmartBEA, setRedirectToSmartBEA] = useState<boolean>(false);
  const history = useHistory();

  const roleAndPermission = useRoleAndPermission();

  const queryVariables = {
    filter,
    sort: { locationName: SortOptions.Asc },
    pagination: {
      offset: (pagination.current - 1) * pagination.pageSize,
      limit: pagination.pageSize,
    },
  };

  // Function to check for pending survey data in IndexedDB
  const checkForPendingSurveyData = async (email: string) => {
    try {
      setCheckingPendingData(true);

      // Set a 5-second timeout to automatically set checkingPendingData to false
      const timeoutId = setTimeout(() => {
        setCheckingPendingData(false);
      }, 5000);

      const surveyData = await getSurveyDataByEmail(email);

      // Clear the timeout if we get a response before 5 seconds
      clearTimeout(timeoutId);

      if (surveyData && surveyData.surveyData) {
        // Set flag to redirect to SmartBEA page
        setRedirectToSmartBEA(true);
        setCheckingPendingData(false);
      } else {
        setCheckingPendingData(false);
      }
    } catch (err) {
      // Using logger instead of console.error
      logger.error("Error checking for pending survey data:", err);
      setCheckingPendingData(false);
    }
  };

  const handleCompletedDataFetching = (data: LocationsForCustomerQuery) => {
    const locations = data.locationsAssignedToUser?.locations || [];
    setTableData(locations);
    // we're only interested in the total from the server, the current and pageSize are managed in local state
    setPagination({
      ...pagination,
      total: data.locationsAssignedToUser?.pagination?.count || 0,
    });
    menuNavigation.setupLocationMenu(locations);

    // Check if there are no locations and the user is authenticated
    if (locations.length === 0 && isAuthenticated && user?.email) {
      checkForPendingSurveyData(user.email);
    } else {
      setCheckingPendingData(false);
    }
  };

  const { loading, error } = useLocationsForCustomerQuery({
    variables: queryVariables,
    onCompleted: handleCompletedDataFetching,
  });

  const handleTableChange = (desiredPagination: AntdTablePagination) => {
    setPagination(desiredPagination);
  };

  const statMessage = useMemo(() => {
    if (tableData.length === 0) return "";
    return getPaginationMessage(pagination);
  }, [tableData, pagination]);

  const handleFilterChange = (newFilter: LocationFilterInput) => {
    setPagination(defaultPagination);
    setFilter(newFilter);
  };

  // Setup location menu when tableData changes
  useEffect(() => {
    if (tableData) {
      menuNavigation.setupLocationMenu(tableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  // Ensure loading state is properly handled
  useEffect(() => {
    if (!loading && !error && !checkingPendingData) {
      setCheckingPendingData(false);
    }
  }, [loading, error, checkingPendingData]);

  // Handle redirection to SmartBEA if needed
  useEffect(() => {
    if (redirectToSmartBEA) {
      // Redirect to the SmartBEA page
      history.push("/smartbea", { skipSurveyForm: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectToSmartBEA]);

  // Show error state with proper role="alert" - prioritize showing errors even when checking for pending data
  if (error) {
    return (
      <div role="alert" className="ant-alert ant-alert-error">
        <span className="ant-alert-message">Error getting locations</span>
        <div className="ant-alert-description" data-testid="error-message">
          {error.message}
        </div>
      </div>
    );
  }

  // If checking for pending data, show a loading spinner
  if (checkingPendingData || loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Spin size="large" />
        <span style={{ marginLeft: "12px" }}>Checking for pending data...</span>
      </div>
    );
  }

  return (
    <>
      <Row className="mb-m">
        <Col>
          <h1 className="d-inline font-weight-bold mr-l">Locations</h1>
          <span className="d-inline">{statMessage}</span>
        </Col>
      </Row>
      <Row className="mb-l">
        <Col span={24}>
          <ListFilter
            currentFilter={filter}
            onChange={handleFilterChange}
            searchField="locationName"
            searchPlaceholder="Type to search"
            hideAdvancedFilter={roleAndPermission.isCustomer()}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {tableData.length === 0 ? (
            <div className="ant-empty">
              <div className="ant-empty-image">
                <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                  <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                    <ellipse fill="#F5F5F5" cx="32" cy="33" rx="32" ry="7" />
                    <g fillRule="nonzero" stroke="#D9D9D9">
                      <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" />
                      <path
                        d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
                        fill="#FAFAFA"
                      />
                    </g>
                  </g>
                </svg>
              </div>
              <p className="ant-empty-description">No data</p>
            </div>
          ) : (
            <LocationListTable
              tableData={tableData}
              pagination={{
                ...pagination,
                pageSizeOption: ["10", "20", "50", "100"],
                showSizeChanger: true,
              }}
              handleTableChange={handleTableChange}
              loading={loading}
              error={error}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default LocationList;
